import { useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface MappingShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  shareToken: string;
  mappingShareEnabled: boolean;
}

export function MappingShareDialog({
  open,
  onOpenChange,
  collectionId,
  shareToken,
  mappingShareEnabled,
}: MappingShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const utils = trpc.useUtils();

  const shareUrl = `${window.location.origin}/mapping/${shareToken}`;

  const enableMutation = trpc.collections.enableMappingShare.useMutation({
    onSuccess: () => {
      utils.collections.getById.invalidate({ id: collectionId });
      utils.collections.list.invalidate();
      toast.success("Mapping share enabled");
    },
    onError: (e) => toast.error(e.message),
  });

  const disableMutation = trpc.collections.disableMappingShare.useMutation({
    onSuccess: () => {
      utils.collections.getById.invalidate({ id: collectionId });
      utils.collections.list.invalidate();
      toast.success("Mapping share disabled");
    },
    onError: (e) => toast.error(e.message),
  });

  async function handleCopy() {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  }

  const handleToggle = (next: boolean) => {
    if (next) enableMutation.mutate({ collectionId });
    else disableMutation.mutate({ collectionId });
  };

  const busy = enableMutation.isPending || disableMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Physical mapping share</DialogTitle>
          <DialogDescription>
            Share this link with physical mappers. They can open it on their phone
            without logging in to complete per-location mapping forms.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Enable mapping share</Label>
              <p className="text-xs text-muted-foreground">
                Allows anyone with the link to fill in mapping data.
              </p>
            </div>
            <Switch
              checked={mappingShareEnabled}
              onCheckedChange={handleToggle}
              disabled={busy}
            />
          </div>

          {mappingShareEnabled && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Share URL
              </Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={shareUrl}
                  className="text-xs h-9"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <Button size="sm" className="h-9 shrink-0" onClick={handleCopy}>
                  {copied ? (
                    <Check className="size-3.5" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-9 shrink-0"
                  asChild
                >
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="size-3.5" />
                  </a>
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
